import { Editor, Plugin } from 'grapesjs';

import { attrsToString, resizerAll, resizerBtm, resizerRight } from '../../plugins/utils';

export const variants = [
  {
    label: 'Content',
    value: 'content',
    className: 'typography-content',
    style: { color: '#474746', 'font-size': '9px', 'line-height': '122.22%', display: 'flex' },
  },
  {
    label: 'Organization Name',
    value: 'orgName',
    className: 'typography-org-name',
    style: { 'font-weight': 600, 'line-height': '150%', 'font-size': '16px', color: '#667239', display: 'flex' },
  },
  {
    label: 'Heading',
    value: 'heading',
    className: 'typography-heading',
    style: { 'letter-spacing': '1px', 'line-height': '118%', 'font-size': '11px', display: 'flex' },
  },
  {
    label: 'Subheading',
    value: 'subheading',
    className: 'typography-subheading',
    style: { 'line-height': '122.22%', 'font-size': '9px', 'font-weight': 500, display: 'flex' },
  },
  {
    label: 'Section Heading',
    value: 'sectionHeading',
    className: 'typography-section-heading',
    style: { color: '#474746', 'font-size': '8px', 'line-height': '137.5%', 'letter-spacing': '1px', display: 'flex' },
  },
  {
    label: 'Total Heading',
    value: 'totalHeading',
    className: 'typography-total-heading',
    style: { color: '#212220', 'font-size': '10px', 'font-weight': 500, 'letter-spacing': '0.5px', display: 'flex' },
  },
  {
    label: 'Total',
    value: 'total',
    className: 'typography-total',
    style: { color: '#212220', 'font-size': '12px', 'font-weight': 600, display: 'flex' },
  },
];

export const textStyles = variants
  .map(({ className, style }) => {
    return `
  .${className} {
    ${Object.entries(style)
      .map(([key, value]) => {
        return `${key}: ${value};`;
      })
      .join('\n')}
  }
`;
  })
  .join('\n');

const plugin =
  (setModalOpen, logoUrl) =>
  (editor: Editor): Plugin => {
    const blockManager = editor.BlockManager;
    const commands = editor.Commands;
    const domComponents = editor.DomComponents;

    commands.add('open-preview', {
      run: (editor) => {
        const pagesHtml = editor.Pages.getAll().map((page) => {
          const component = page.getMainComponent();
          return { html: editor.getHtml({ component }), css: editor.getCss({ component }) };
        });

        // right now, just hardcoding to page 0. Support for multiple pages will be added shortly
        setModalOpen(pagesHtml[0]);
      },
    });

    const imgAttrs = attrsToString({ 'data-gjs-resizable': resizerAll });

    blockManager.add('orgLogo', {
      label: 'Organization Logo',
      content: `<div style="width: 100px; height: 100px"><img ${imgAttrs} data-gjs-type="orgLogo" /></div>`,
      media: `<svg viewBox="0 0 24 24">
    <path fill="currentColor" d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z" />
  </svg>`,
      category: 'Basic',
    });

    domComponents.addType('orgLogo', {
      model: {
        defaults: { tagName: 'img' },
        toHTML: function () {
          return `<img id="${this.ccid}" src="{{{orgLogo}}}" />`;
        },
      },
      view: {
        onRender() {
          this.el.src = logoUrl;
        },
      },
    });

    blockManager.add('header', {
      label: 'Header Block',
      content: `
    <div class="header">
      <div class="header-row">
        <div style="display: flex; flex-direction: row" >
          <div style="width: 26px; height: 26px" >
            <img data-gjs-type="orgLogo" />
          </div>
          <div>
            <div data-gjs-type="variable" >{{organization.name}}</div>
          </div>
        </div>
        <div>
          <div data-gjs-type="variable" >{{organization.name}}</div>
        </div>
      </div>
      <div class="header-row">
        <div>
          <div data-gjs-type="variable" ></div>
        </div>
        <div>
          <div data-gjs-type="variable" ></div>
        </div>
      </div>
    </div>
      <style>
        .header {
          box-sizing: border-box;

          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 24px 24px 16px;

          width: 100%;
          height: 121px;

          background: #F7F8F3;
        }
        .header-row {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        }
      </style>
    `,
      media: `
      <svg viewBox="0 0 24 24">
        <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z" />
      </svg>
    `,
      category: 'Basic',
    });

    blockManager.add('list', {
      label: 'List',
      content: `<div data-gjs-type="list"></div>`,
      media: `
      <svg viewBox="0 0 24 24">
        <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z" />
      </svg>
    `,
      category: 'Basic',
    });

    domComponents.addType('list', {
      model: {
        defaults: {
          tagName: 'div',
          droppable: true,
          draggable: true,
          attributes: { 'data-gjs-type': 'list' },
          traits: [{ type: 'text', label: 'List', name: 'list', changeProp: true }],
        },
        init() {
          this.listenTo(this, 'change:list', this.handleContentChange);
        },
        handleContentChange() {
          this.view.render();
        },
        toHTML: function () {
          const list = this.get('list');
          const children = this.components();
          let childrenHtml = '';

          if (children) {
            children.forEach((child) => {
              childrenHtml += child.toHTML();
            });
          }

          const classes = this.get('classes').pluck('name').join(' ');

          return `{{#${list}}}
                  <div class="${classes}">
                    ${childrenHtml}
                    </div>
                {{/${list}}}`;
        },
      },
    });

    const commonBlockProps = { category: 'Basic', attributes: { class: 'gjs-fonts gjs-f-b1' } };

    const clsRow = 'layout-row';
    const clsCell = 'layout-cell';

    const rowAttr = { class: clsRow, 'data-gjs-resizable': resizerBtm, 'data-gjs-name': 'Row' };

    const colAttr: Record<string, string | unknown> = {
      class: clsCell,
      'data-gjs-draggable': `.${clsRow}`,
      'data-gjs-resizable': { ...resizerRight, keyWidth: 'flex-basis', minDim: 0 },
      'data-gjs-name': 'Cell',
    };

    const privateCls = [`.${clsRow}`, `.${clsCell}`];

    editor.on(
      'selector:add',
      (selector) => privateCls.indexOf(selector.getFullName()) >= 0 && selector.set('private', 1)
    );

    const attrsRow = attrsToString(rowAttr);
    const attrsCell = attrsToString(colAttr);

    const styleRow = `
    .${clsRow} {
      flex-direction: row;
      display: flex;
      justify-content: flex-start;
      align-items: stretch;
      flex-wrap: nowrap;
    }
    `;

    const styleClm = `
    .${clsCell} {
      min-height: ${10}px;
      text-wrap: break-word;
      font-size: 13px;
      flex: 1;
    }`;

    blockManager.add('pageBody', {
      ...commonBlockProps,
      label: 'Page Body',

      content: `
      <section class="pageBody" data-gjs-type="pageBody">
      </section>
      <style>
        .pageBody {
          padding: 24px 24px 16px;
          min-height: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      </style>
    `,
    });

    domComponents.addType('pageBody', {
      model: {
        defaults: {
          tagName: 'section',
          attributes: { class: 'pageBody' },
          components: [{ type: 'block', content: '<div></div>' }],
        },
      },
    });

    blockManager.add('address', {
      label: 'Address',
      content: `<div data-gjs-type="address"></div>`,
      media: `
      <svg viewBox="0 0 24 24">
        <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z" />
      </svg>
    `,
      category: 'Basic',
    });

    const optionalSpan = (content, attributes = '') => {
      return `{{#${content}}}
        <div ${attributes}>
          <span>{{{${content}}}}</span>
        </div>
      {{/${content}}}`;
    };

    domComponents.addType('address', {
      model: {
        defaults: {
          tagName: 'div',
          droppable: true,
          draggable: true,
          attributes: { 'data-gjs-type': 'address' },
          traits: [
            { type: 'text', label: 'Address', name: 'address', changeProp: true },
            { type: 'text', label: 'Header Name', name: 'header', changeProp: true, default: '' },
            { type: 'checkbox', label: 'Description as Header', name: 'descriptionHeader', changeProp: true },
            { type: 'checkbox', label: 'Hide Header', name: 'hideHeader', default: false, changeProp: true },
          ],
        },
        init() {
          this.listenTo(this, 'change:address', this.handleContentChange);
          this.listenTo(this, 'change:hideHeader', this.handleContentChange);
        },
        handleContentChange() {
          this.view.render();
        },
        toHTML: function () {
          const address = this.get('address');
          const boldHeader = this.get('descriptionHeader');
          const headerName = this.get('header');
          const headerClass = boldHeader ? 'typography-subheading' : '';
          const classes = this.get('classes').pluck('name').join(' ');
          const showHeader = !this.get('hideHeader');

          return `
        <div id="${this.ccid}" class="${classes}" style="display: flex; flex-direction: column; gap: 2px">
          ${showHeader ? optionalSpan(`${address}.description`, `class="${headerClass}"`) : ''}
          {{^${address}.description}}
            <span class=${headerClass}>${headerName}</span>
          {{/${address}.description}}
          ${optionalSpan(`${address}.streetAddress1`)}
          {{#${address}.hasLineTwo}}
            <div style="display: flex; flex-direction: row; flex-wrap: wrap;">
              ${optionalSpan(`${address}.city`)}
              ${optionalSpan(`${address}.state`)}
              ${optionalSpan(`${address}.postalCode`)}
            </div>
          {{/${address}.hasLineTwo}}
          {{^_settings.hideAddressCountry}}
            ${optionalSpan(`${address}.addressCountry`)}
          {{/_settings.hideAddressCountry}}
        </div>`;
        },
      },
      view: {
        onRender: function () {
          const address = this.model.get('address');

          this.el.innerHTML = `<div>${address}</div>`;
        },
      },
    });

    blockManager.add('column2', {
      ...commonBlockProps,
      label: 'Columns',
      media: `
      <svg viewBox="0 0 23 24">
        <path fill="currentColor" d="M2 20h8V4H2v16Zm-1 0V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1ZM13 20h8V4h-8v16Zm-1 0V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1Z"/>
      </svg>`,

      content: `<div ${attrsRow}>
      <div ${attrsCell} ></div>
      <div ${attrsCell}></div>
    </div>
    ${`<style>
        ${styleRow}
        ${styleClm}
      </style>`}`,
    });

    blockManager.add('row', {
      label: 'Row',
      category: 'Basic',
      content: [
        {
          type: 'block',
          attributes: { class: 'section-row' },
          styles: '.section-row { display: flex; flex-direction: row; gap: 4px; }',
        },
      ],
    });

    editor.DomComponents.addType('text', {
      model: {
        defaults: {
          tagName: 'span',
          styles: textStyles,
          traits: [
            {
              type: 'select',
              label: 'Variant',
              name: 'variant',
              options: variants.map(({ label, value }) => {
                return { id: value, label };
              }),
              changeProp: true,
              default: 'content',
            },
          ],
        },

        init() {
          this.listenTo(this, 'change:variant', this.handleTypographyClassChange);
        },

        handleTypographyClassChange() {
          const selectedType = this.get('variant');

          variants.forEach(({ className }) => this.removeClass(className));

          if (selectedType) {
            const className = variants.find((v) => v.value === selectedType)?.className;

            this.addClass(className);
          }
        },
      },
    });

    blockManager.add('text', {
      ...commonBlockProps,
      activate: true,
      label: 'Text',
      content: { type: 'text', content: `Insert content here` },
    });

    domComponents.addType('text', {
      model: {
        defaults: {
          tagName: 'span',
          styles: textStyles,
          traits: [
            {
              type: 'select',
              label: 'Variant',
              name: 'variant',
              options: variants.map(({ label, value, style }) => {
                return { id: value, label, style };
              }),
              changeProp: true,
              default: 'content',
            },
          ],
        },
      },
    });

    blockManager.add('icon', {
      category: 'Basic',
      activate: true,
      label: 'Icon',

      media: `
      <svg viewBox="0 0 24 24" verticalAlign=middle;>
        <path fill="currentColor" d="M18.5,4L19.66,8.35L18.7,8.61C18.25,7.74 17.79,6.87 17.26,6.43C16.73,6 16.11,6 15.5,6H13V16.5C13,17 13,17.5 13.33,17.75C13.67,18 14.33,18 15,18V19H9V18C9.67,18 10.33,18 10.67,17.75C11,17.5 11,17 11,16.5V6H8.5C7.89,6 7.27,6 6.74,6.43C6.21,6.87 5.75,7.74 5.3,8.61L4.34,8.35L5.5,4H18.5Z" />
      </svg>`,
      content: { type: 'icon' },
    });

    domComponents.addType('icon', {
      model: {
        defaults: {
          tagName: 'svg',
          droppable: true,
          draggable: true,
          attributes: { 'data-gjs-type': 'icon' },
          traits: [
            {
              type: 'select',
              label: 'Icon Name',
              name: 'icon',
              default: 'mail',
              options: [
                { id: 'mail', name: 'Mail' },
                { id: 'call', name: 'Call' },
                { id: 'shipment', name: 'Shipment' },
              ],
              changeProp: true,
            },
          ],
        },
        init() {
          this.listenTo(this, 'change:icon', this.handleContentChange);
        },
        handleContentChange() {
          this.view.render();
        },
        toHTML: function () {
          const iconType = this.get('icon');
          const icon = icons[iconType] ?? {};

          return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${icon.width}" height="${icon.height}" viewBox="${icon.viewBox}" verticalAlign="top" fill="#667239">
          <path d="${icon.path}"/>
        </svg>
        `;
        },
      },
      view: {
        onRender: function () {
          const iconType = this.model.get('icon');
          const icon = icons[iconType] ?? {};

          this.el.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${icon.width}" height="${icon.height}" viewBox="${icon.viewBox}" verticalAlign="top" fill="#667239">
          <path d="${icon.path}"/>
        </svg>`;
        },
      },
    });

    editor.BlockManager.add('hide-if-empty', {
      label: 'Hide if list is empty',
      content: { type: 'hide-if-empty' },
      category: 'Basic',
    });

    editor.DomComponents.addType('hide-if-empty', {
      view: {
        onRender() {
          this.el.style.display = 'contents';
        },
      },
      model: {
        defaults: {
          tagName: 'div',
          droppable: true,

          traits: [{ label: 'Variable', type: 'text', name: 'variable', default: '' }],
        },

        toHTML: function () {
          const variable = this.get('traits')?.get('variable');
          const variableValue = variable?.getValue();

          let childrenHtml = '';

          this.components().each((model) => {
            childrenHtml += model.toHTML();
          });

          return `
        {{^${variableValue}.isEmpty}}
          ${childrenHtml}
        {{/${variableValue}.isEmpty}}
        `;
        },
      },
    });

    editor.BlockManager.add('hide-if-empty-2', {
      label: 'Hide if value is falsey',
      content: { type: 'hide-if-empty-2' },
      category: 'Basic',
    });

    editor.DomComponents.addType('hide-if-empty-2', {
      view: {
        onRender() {
          this.el.style.display = 'contents';
        },
      },
      model: {
        defaults: {
          tagName: 'div',
          droppable: true,
          traits: [{ label: 'Variable', type: 'text', name: 'variable', changeProp: true }],
        },
        toHTML: function () {
          const variable = this.get('traits')?.get('variable');
          const variableValue = variable?.getValue();

          let childrenHtml = '';

          this.components().each((model) => {
            childrenHtml += model.toHTML();
          });

          return `
        {{#${variableValue}}}
          ${childrenHtml}
        {{/${variableValue}}}
        `;
        },
      },
    });

    editor.BlockManager.add('hide-if-true', {
      label: 'Hide if value is truthy',
      content: { type: 'hide-if-true' },
      category: 'Basic',
    });

    editor.DomComponents.addType('hide-if-true', {
      view: {
        onRender() {
          this.el.style.display = 'contents';
        },
      },
      model: {
        defaults: {
          tagName: 'div',
          droppable: true,
          traits: [{ label: 'Variable', type: 'text', name: 'variable', changeProp: true }],
        },
        toHTML: function () {
          const variable = this.get('traits')?.get('variable');
          const variableValue = variable?.getValue();

          let childrenHtml = '';

          this.components().each((model) => {
            childrenHtml += model.toHTML();
          });

          return `
        {{^${variableValue}}}
          ${childrenHtml}
        {{/${variableValue}}}
        `;
        },
      },
    });

    blockManager.add('spacer', {
      label: 'Spacer',
      content: `<div data-gjs-type="spacer"></div>`,
      media: `
      <svg viewBox="0 0 24 24">
        <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z" />
      </svg>
    `,
      category: 'Basic',
    });

    domComponents.addType('spacer', {
      view: {
        onRender: function () {
          this.el.innerHTML = `<div
            style="border: 1px dashed #E0E0E0; text-align: center; color:rgb(149, 149, 149); font-style: italic; letter-spacing: 5px; font-size: 10px; padding: 10px;
            background: repeating-linear-gradient(45deg, transparent, transparent 10px, #E0E0E0 10px, #E0E0E0 11px);"
          >
          </div>`;
        },
      },
      model: {
        toHTML: function () {
          return `<div style="margin-top: auto" />`;
        },
      },
    });

    editor.BlockManager.add('custom-image', {
      label: 'Image',
      category: 'Basic',
      content: {
        type: 'image-url',
        attributes: {
          src: '', // optional initial value
        },
        traits: [{ type: 'text', name: 'src', label: 'Image URL', placeholder: 'https://example.com/image.jpg' }],
      },
    });

    domComponents.addType('image-url', {
      model: {
        defaults: {
          tagName: 'img',
          droppable: false,
          attributes: { src: 'https://via.placeholder.com/300x150?text=Image' },
          traits: [{ type: 'text', name: 'src', label: 'Image URL' }],
        },

        init() {
          this.listenTo(this, 'change:attributes:src', this.convertSrcToBase64);
        },

        // to avoid CORS issues, convert external image URLs to base64
        async convertSrcToBase64() {
          const url = this.getAttributes().src;
          if (!url || url.startsWith('data:')) return;

          try {
            const res = await fetch(url);
            const contentType = res.headers.get('Content-Type') || 'image/png';
            const buffer = await res.arrayBuffer();
            const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
            const dataUrl = `data:${contentType};base64,${base64}`;

            this.setAttributes({
              src: dataUrl,
              'data-original-src': url, // optional: preserve original
            });
          } catch (err) {
            console.error('Failed to fetch image:', err);
          }
        },
      },
    });
  };

// Just hardcoding a few icons for now.
const icons = {
  mail: {
    viewBox: '0 0 9 8',
    width: 9,
    height: 8,
    path: 'M7.83331 0.666687H1.16665C0.708313 0.666687 0.33748 1.04169 0.33748 1.50002L0.333313 6.50002C0.333313 6.95835 0.708313 7.33335 1.16665 7.33335H7.83331C8.29165 7.33335 8.66665 6.95835 8.66665 6.50002V1.50002C8.66665 1.04169 8.29165 0.666687 7.83331 0.666687ZM7.83331 2.33335L4.49998 4.41669L1.16665 2.33335V1.50002L4.49998 3.58335L7.83331 1.50002V2.33335Z',
  },
  call: {
    viewBox: '0 0 9 8',
    width: 9,
    height: 7,
    path: 'M7.8375 5.40833C7.325 5.40833 6.82917 5.325 6.36667 5.175C6.22083 5.125 6.05833 5.1625 5.94583 5.275L5.29167 6.09583C4.1125 5.53333 3.00833 4.47083 2.42083 3.25L3.23333 2.55833C3.34583 2.44167 3.37917 2.27917 3.33333 2.13333C3.17917 1.67083 3.1 1.175 3.1 0.6625C3.1 0.4375 2.9125 0.25 2.6875 0.25H1.24583C1.02083 0.25 0.75 0.35 0.75 0.6625C0.75 4.53333 3.97083 7.75 7.8375 7.75C8.13333 7.75 8.25 7.4875 8.25 7.25833V5.82083C8.25 5.59583 8.0625 5.40833 7.8375 5.40833Z',
  },
  shipment: {
    viewBox: '0 0 11 8',
    width: 11,
    height: 8,
    path: 'M2.5 8C2.08333 8 1.72917 7.85417 1.4375 7.5625C1.14583 7.27083 1 6.91667 1 6.5H0V1C0 0.725 0.0979167 0.489583 0.29375 0.29375C0.489583 0.0979167 0.725 0 1 0H8V2H9.5L11 4V6.5H10C10 6.91667 9.85417 7.27083 9.5625 7.5625C9.27083 7.85417 8.91667 8 8.5 8C8.08333 8 7.72917 7.85417 7.4375 7.5625C7.14583 7.27083 7 6.91667 7 6.5H4C4 6.91667 3.85417 7.27083 3.5625 7.5625C3.27083 7.85417 2.91667 8 2.5 8ZM2.5 7C2.64167 7 2.76042 6.95208 2.85625 6.85625C2.95208 6.76042 3 6.64167 3 6.5C3 6.35833 2.95208 6.23958 2.85625 6.14375C2.76042 6.04792 2.64167 6 2.5 6C2.35833 6 2.23958 6.04792 2.14375 6.14375C2.04792 6.23958 2 6.35833 2 6.5C2 6.64167 2.04792 6.76042 2.14375 6.85625C2.23958 6.95208 2.35833 7 2.5 7ZM1 5.5H1.4C1.54167 5.35 1.70417 5.22917 1.8875 5.1375C2.07083 5.04583 2.275 5 2.5 5C2.725 5 2.92917 5.04583 3.1125 5.1375C3.29583 5.22917 3.45833 5.35 3.6 5.5H7V1H1V5.5ZM8.5 7C8.64167 7 8.76042 6.95208 8.85625 6.85625C8.95208 6.76042 9 6.64167 9 6.5C9 6.35833 8.95208 6.23958 8.85625 6.14375C8.76042 6.04792 8.64167 6 8.5 6C8.35833 6 8.23958 6.04792 8.14375 6.14375C8.04792 6.23958 8 6.35833 8 6.5C8 6.64167 8.04792 6.76042 8.14375 6.85625C8.23958 6.95208 8.35833 7 8.5 7ZM8 4.5H10.125L9 3H8V4.5Z',
  },
};

export default plugin;
